import { Controller } from "@hotwired/stimulus"
import L from "leaflet"
import "leaflet/dist/leaflet.css"

/**
 * Interactive map controller for displaying EXIF GPS data
 */
export default class extends Controller {
  static values = {
    latitude: Number,
    longitude: Number,
    zoom: { type: Number, default: 14 }
  }

  connect() {
    // Create map only after the element is visible to ensure proper rendering
    if (this.element.offsetParent !== null) {
      this.initializeMap()
    } else {
      // Set up a mutation observer to detect when the element becomes visible
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.target.style.display !== 'none' && !this.map) {
            this.initializeMap()
          }
        })
      })
      
      // Start observing style changes on parent elements
      let parent = this.element.closest('.document-app__indicator-content')
      if (parent) {
        this.observer.observe(parent, { attributes: true, attributeFilter: ['style'] })
      }
    }
  }
  
  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
    
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  initializeMap() {
    if (this.map) return // Don't initialize twice
    
    // Fix Leaflet's icon path issues
    this.fixLeafletIcons()
    
    // Initialize the map
    this.map = L.map(this.element, {
      center: [this.latitudeValue, this.longitudeValue],
      zoom: this.zoomValue,
      scrollWheelZoom: false,
      dragging: true, // Allow dragging for better exploration
      zoomControl: true // Allow zooming for better user experience
    })

    // Add OpenStreetMap tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(this.map)

    // Add a marker at the GPS coordinates
    const marker = L.marker([this.latitudeValue, this.longitudeValue]).addTo(this.map)
    
    // Add a popup with the coordinates
    marker.bindPopup(`${this.latitudeValue.toFixed(6)}, ${this.longitudeValue.toFixed(6)}`)
    
    // Fix map size issues after initial render
    setTimeout(() => {
      this.map.invalidateSize()
    }, 100)
  }
  
  // Fix Leaflet icon path issues (common problem in Rails/webpack environments)
  fixLeafletIcons() {
    delete L.Icon.Default.prototype._getIconUrl
    
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
      shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png'
    })
  }
}